import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import UploadZone from "@/components/UploadZone";
import ResultsTable from "@/components/ResultsTable";
import { Package, Truck, CircleDollarSign, Loader2, Search, AlertTriangle, Wallet, FileDown } from "lucide-react";

const FILTERS = [
  { id: "all", label: "Toate" },
  { id: "delivered", label: "Livrate" },
  { id: "notdelivered", label: "Nelivrate" },
  { id: "paid", label: "Plătite" },
  { id: "unpaid", label: "Neplătite" },
];

function Stat({ icon: Icon, label, value, color }) {
  return (
    <div className="border border-border rounded-sm bg-card p-4">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
        <Icon size={16} style={{ color }} />
      </div>
      <div className="mt-2 text-3xl font-mono font-semibold tracking-tight" style={{ color }}>
        {value}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [awbFiles, setAwbFiles] = useState([]);
  const [decontFiles, setDecontFiles] = useState([]);
  const [batch, setBatch] = useState(null);
  const [loadingAwb, setLoadingAwb] = useState(false);
  const [loadingPay, setLoadingPay] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");

  const stats = useMemo(() => {
    const a = batch?.awbs || [];
    const uncollected = a
      .filter((x) => x.delivered && x.paid === false)
      .reduce((s, x) => s + (parseFloat(String(x.amount || "0").replace(",", ".")) || 0), 0);
    return {
      total: a.length,
      delivered: a.filter((x) => x.delivered).length,
      notdelivered: a.filter((x) => !x.delivered).length,
      paid: a.filter((x) => x.paid === true).length,
      unpaid: a.filter((x) => x.paid === false).length,
      uncollected,
    };
  }, [batch]);

  const filtered = useMemo(() => {
    let a = batch?.awbs || [];
    if (filter === "delivered") a = a.filter((x) => x.delivered);
    if (filter === "notdelivered") a = a.filter((x) => !x.delivered);
    if (filter === "paid") a = a.filter((x) => x.paid === true);
    if (filter === "unpaid") a = a.filter((x) => x.paid === false);
    if (query.trim()) a = a.filter((x) => x.awb.includes(query.trim()));
    return a;
  }, [batch, filter, query]);

  const multiSource = useMemo(() => {
    const m = {};
    (batch?.awbs || []).forEach((a) => {
      const k = a.source || "—";
      m[k] = (m[k] || 0) + 1;
    });
    return Object.entries(m).filter(([, c]) => c > 1);
  }, [batch]);

  const verifyAwbs = async () => {
    if (!awbFiles.length) return;
    if (!user?.has_cargus) {
      toast.error("Configurează credențialele Cargus în Setări");
      navigate("/setari");
      return;
    }
    setLoadingAwb(true);
    try {
      const fd = new FormData();
      awbFiles.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/verify/awbs", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBatch(data);
      toast.success(`${data.awbs.length} AWB-uri verificate`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoadingAwb(false);
    }
  };

  const verifyPayments = async () => {
    if (!batch) {
      toast.error('Întâi încarcă folderul cu AWB-uri la Pas 1 și apasă „Verifică livrarea".');
      return;
    }
    if (!decontFiles.length) {
      toast.error("Adaugă fișierele cu deconturi.");
      return;
    }
    setLoadingPay(true);
    try {
      const fd = new FormData();
      decontFiles.forEach((f) => fd.append("files", f));
      if (fromDate) fd.append("from_date", fromDate);
      if (toDate) fd.append("to_date", toDate);
      const { data } = await api.post(`/verify/${batch.id}/payments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBatch(data);
      toast.success("Plățile au fost verificate");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoadingPay(false);
    }
  };

  const hasPayments = (batch?.awbs || []).some((a) => a.paid !== null && a.paid !== undefined);

  const downloadUnpaidPdf = async () => {
    try {
      const res = await api.get(`/batches/${batch.id}/unpaid-pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = "comenzi_neplatite.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Nu s-a putut genera PDF-ul");
    }
  };

  return (
    <Layout>
      <div className="p-8 max-w-[1400px] mx-auto">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">Verificare AWB-uri</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Încarcă PDF-urile cu AWB-uri, verifică livrarea, apoi adaugă deconturile pentru plată.
            </p>
          </div>
        </div>

        {!user?.has_cargus && (
          <div className="mb-6 flex items-center gap-3 border border-[#FFCC00]/30 bg-[#FFCC00]/10 rounded-sm px-4 py-3 text-sm text-[#FFCC00]">
            <AlertTriangle size={16} />
            Nu ai configurat credențialele Cargus.
            <button className="underline" onClick={() => navigate("/setari")} data-testid="go-settings">
              Mergi la Setări
            </button>
          </div>
        )}

        {/* Step 1 + 2 */}
        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          <div className="border border-border rounded-sm bg-card p-5">
            <div className="flex items-center gap-2 mb-1">
              <Package size={16} />
              <h2 className="text-base font-semibold">Pas 1 · AWB-uri</h2>
            </div>
            <p className="text-xs text-muted-foreground mb-4 font-mono">Verifică ce e livrat și ce nu.</p>
            <UploadZone testid="awb-upload" files={awbFiles} setFiles={setAwbFiles} />
            <button
              data-testid="verify-awb-btn"
              disabled={!awbFiles.length || loadingAwb}
              onClick={verifyAwbs}
              className="mt-4 w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-sm px-3 py-2.5 text-sm font-medium hover:bg-white/90 transition-colors disabled:opacity-50"
            >
              {loadingAwb ? <Loader2 size={16} className="animate-spin" /> : <Truck size={16} />}
              Verifică livrarea
            </button>
          </div>

          <div className="border border-border rounded-sm bg-card p-5">
            <div className="flex items-center gap-2 mb-1">
              <Wallet size={16} />
              <h2 className="text-base font-semibold">Pas 2 · Deconturi</h2>
            </div>
            <p className="text-xs text-muted-foreground mb-4 font-mono">Verifică ce ramburs e plătit.</p>
            {!batch && (
              <div className="mb-4 flex items-start gap-2 border border-[#FFCC00]/30 bg-[#FFCC00]/10 rounded-sm px-3 py-2 text-xs text-[#FFCC00]">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                <span>Întâi rulează <b>Pas 1 · Verifică livrarea</b>. Abia apoi pot fi comparate deconturile.</span>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3 mb-2">
              <div>
                <label className="text-[10px] text-muted-foreground uppercase tracking-wider">De la data (decont)</label>
                <input
                  data-testid="from-date"
                  type="text"
                  inputMode="numeric"
                  placeholder="zz.ll.aaaa"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="mt-1 w-full bg-card border border-border rounded-sm px-2.5 py-2 text-sm font-mono outline-none focus:border-primary"
                />
              </div>
              <div>
                <label className="text-[10px] text-muted-foreground uppercase tracking-wider">Până la data</label>
                <input
                  data-testid="to-date"
                  type="text"
                  inputMode="numeric"
                  placeholder="zz.ll.aaaa"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="mt-1 w-full bg-card border border-border rounded-sm px-2.5 py-2 text-sm font-mono outline-none focus:border-primary"
                />
              </div>
            </div>
            <p className="text-[10px] text-muted-foreground font-mono mb-4">
              Scrie liber: ex. 12.09.2027 (zz.ll.aaaa). Lasă gol pentru fără filtru.
            </p>
            <UploadZone
              testid="decont-upload"
              files={decontFiles}
              setFiles={setDecontFiles}
              exts={[".pdf", ".xls", ".xlsx"]}
              accept=".pdf,.xls,.xlsx"
              hint="PDF sau Excel (.xls/.xlsx) cu deconturile Cargus"
            />
            <button
              data-testid="verify-pay-btn"
              disabled={loadingPay}
              onClick={verifyPayments}
              className="mt-4 w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-sm px-3 py-2.5 text-sm font-medium hover:bg-white/90 transition-colors disabled:opacity-50"
            >
              {loadingPay ? <Loader2 size={16} className="animate-spin" /> : <CircleDollarSign size={16} />}
              Verifică plata
            </button>
          </div>
        </div>

        {batch && (
          <div className="animate-fade-up">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
              <Stat icon={Package} label="Total AWB" value={stats.total} color="#FFFFFF" />
              <Stat icon={Truck} label="Livrate" value={stats.delivered} color="#34C759" />
              <Stat icon={AlertTriangle} label="Nelivrate" value={stats.notdelivered} color="#FF3B30" />
              <Stat icon={CircleDollarSign} label="Plătite" value={stats.paid} color="#4DA3FF" />
              <Stat icon={Wallet} label="Neplătite" value={stats.unpaid} color="#FFCC00" />
              <Stat
                icon={Wallet}
                label="Neîncasat (lei)"
                value={stats.uncollected.toFixed(2)}
                color="#FF9F0A"
              />
            </div>

            <div className="mb-4 text-xs font-mono text-muted-foreground">
              {batch.awb_files.length} fișiere · {stats.total} AWB-uri extrase
            </div>

            {multiSource.length > 0 && (
              <div
                data-testid="multi-source-hint"
                className="mb-4 flex items-start gap-3 border border-[#FFCC00]/30 bg-[#FFCC00]/10 rounded-sm px-4 py-3 text-xs text-[#FFCC00]"
              >
                <AlertTriangle size={15} className="mt-0.5 shrink-0" />
                <div className="font-mono">
                  <div className="mb-1 font-semibold">
                    Aceste fișiere au generat mai mult de un AWB (aici e probabil AWB-ul în plus):
                  </div>
                  {multiSource.map(([name, count]) => (
                    <div key={name}>
                      • {name} → {count} AWB-uri
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3 mb-4">
              <div className="flex gap-1.5">
                {FILTERS.map((f) => (
                  <button
                    key={f.id}
                    data-testid={`filter-${f.id}`}
                    onClick={() => setFilter(f.id)}
                    className={`px-3 py-1.5 rounded-sm text-xs font-mono border transition-colors ${
                      filter === f.id
                        ? "bg-primary text-primary-foreground border-primary"
                        : "border-border text-muted-foreground hover:text-foreground hover:border-muted-foreground"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              <div className="relative ml-auto">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  data-testid="search-awb"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Caută AWB..."
                  className="bg-card border border-border rounded-sm pl-9 pr-3 py-1.5 text-sm font-mono outline-none focus:border-primary w-56"
                />
              </div>
              {hasPayments && (
                <button
                  data-testid="download-unpaid-btn"
                  onClick={downloadUnpaidPdf}
                  className="flex items-center gap-2 border border-[#FFCC00]/40 text-[#FFCC00] rounded-sm px-3 py-1.5 text-xs font-mono hover:bg-[#FFCC00]/10 transition-colors"
                >
                  <FileDown size={14} />
                  PDF comenzi neplătite ({stats.unpaid})
                </button>
              )}
            </div>

            <ResultsTable awbs={filtered} />
          </div>
        )}
      </div>
    </Layout>
  );
}
